/**
 * PathsSection — segment selector: SWE→AI Engineer, DA/DS→AI/ML,
 * PM→AI Product (primary, per the audience-research two-segment split),
 * plus New grad as a fourth, visually secondary tab.
 *
 * Tab pattern mirrors CareerTransitionHome.tsx's audienceKey state
 * (useState<TransitionAudience>) — same shape, kept local here since this
 * section isn't the Career Transition product page.
 */

import { useState } from "react";
import { Box, Chip, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

type PathKey = "swe" | "ds" | "pm" | "grad";

interface PathContent {
  key: PathKey;
  tabTitleKey: string;
  tabSubKey: string;
  flowKey: string;
  descKey: string;
  gapKeys: string[];
  outKeys: string[];
  secondary?: boolean;
  chipKey?: string;
}

const PATHS: PathContent[] = [
  {
    key: "swe",
    tabTitleKey: "PathsSection.sweTabTitle",
    tabSubKey: "PathsSection.sweTabSub",
    flowKey: "PathsSection.sweFlow",
    descKey: "PathsSection.sweDesc",
    gapKeys: ["PathsSection.sweGap1", "PathsSection.sweGap2", "PathsSection.sweGap3"],
    outKeys: ["PathsSection.sweOut1", "PathsSection.sweOut2", "PathsSection.sweOut3"],
  },
  {
    key: "ds",
    tabTitleKey: "PathsSection.dsTabTitle",
    tabSubKey: "PathsSection.dsTabSub",
    flowKey: "PathsSection.dsFlow",
    descKey: "PathsSection.dsDesc",
    gapKeys: ["PathsSection.dsGap1", "PathsSection.dsGap2", "PathsSection.dsGap3"],
    outKeys: ["PathsSection.dsOut1", "PathsSection.dsOut2", "PathsSection.dsOut3"],
  },
  {
    key: "pm",
    tabTitleKey: "PathsSection.pmTabTitle",
    tabSubKey: "PathsSection.pmTabSub",
    flowKey: "PathsSection.pmFlow",
    descKey: "PathsSection.pmDesc",
    gapKeys: ["PathsSection.pmGap1", "PathsSection.pmGap2", "PathsSection.pmGap3"],
    outKeys: ["PathsSection.pmOut1", "PathsSection.pmOut2", "PathsSection.pmOut3"],
  },
  {
    key: "grad",
    tabTitleKey: "PathsSection.gradTabTitle",
    tabSubKey: "PathsSection.gradTabSub",
    flowKey: "PathsSection.gradFlow",
    descKey: "PathsSection.gradDesc",
    gapKeys: ["PathsSection.gradGap1", "PathsSection.gradGap2", "PathsSection.gradGap3"],
    outKeys: ["PathsSection.gradOut1", "PathsSection.gradOut2", "PathsSection.gradOut3"],
    secondary: true,
    chipKey: "PathsSection.gradChip",
  },
];

export default function PathsSection() {
  const { t } = useTranslation("home");
  const [active, setActive] = useState<PathKey>("swe");
  const current = PATHS.find((p) => p.key === active)!;

  return (
    <Box id="paths" component="section" sx={{ scrollMarginTop: "80px" }}>
      <Typography
        component="span"
        sx={{ fontSize: 14, fontWeight: 500, color: "secondary.main", display: "block", mb: 1 }}
      >
        {t("PathsSection.eyebrow")}
      </Typography>
      <Typography component="h2" sx={{ fontSize: 32, fontWeight: 500, m: 0, mb: 4 }}>
        {t("PathsSection.heading")}
      </Typography>

      <Tabs
        value={active}
        onChange={(_e, val: PathKey) => setActive(val)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        {PATHS.map((p) => (
          <Tab
            key={p.key}
            value={p.key}
            label={
              <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: p.secondary ? 0.85 : 1 }}>
                <Stack alignItems="flex-start">
                  <Typography sx={{ fontSize: 13.5, fontWeight: 500, textTransform: "none" }}>
                    {t(p.tabTitleKey)}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 11.5, fontFamily: '"JetBrains Mono", ui-monospace, monospace', color: "text.secondary", textTransform: "none" }}
                  >
                    {t(p.tabSubKey)}
                  </Typography>
                </Stack>
                {p.chipKey && (
                  <Chip
                    label={t(p.chipKey)}
                    size="small"
                    sx={{ fontSize: 10, height: 18 }}
                  />
                )}
              </Stack>
            }
          />
        ))}
      </Tabs>

      <Box
        sx={{
          p: { xs: 2.5, md: 3.5 },
          borderRadius: "16px",
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 500, mb: 1 }}>{t(current.flowKey)}</Typography>
        <Typography sx={{ color: "text.secondary", fontSize: 15, mb: 3, maxWidth: "60ch" }}>
          {t(current.descKey)}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 3 }}>
          <Box>
            <Typography
              component="h4"
              sx={{ fontSize: 11.5, fontWeight: 500, letterSpacing: "0.04em", color: "text.secondary", mb: 1, m: 0 }}
            >
              {t("PathsSection.gapsColumnHeading")}
            </Typography>
            <Stack component="ul" spacing={0.75} sx={{ m: 0, mt: 1, p: 0, listStyle: "none" }}>
              {current.gapKeys.map((k) => (
                <Typography component="li" key={k} sx={{ fontSize: 14 }}>
                  {t(k)}
                </Typography>
              ))}
            </Stack>
          </Box>
          <Box>
            <Typography
              component="h4"
              sx={{ fontSize: 11.5, fontWeight: 500, letterSpacing: "0.04em", color: "text.secondary", mb: 1, m: 0 }}
            >
              {t("PathsSection.evidenceColumnHeading")}
            </Typography>
            <Stack component="ul" spacing={0.75} sx={{ m: 0, mt: 1, p: 0, listStyle: "none" }}>
              {current.outKeys.map((k) => (
                <Typography component="li" key={k} sx={{ fontSize: 14 }}>
                  {t(k)}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
